/**
 * Registers the shared service worker with the studio's own scope (/s/<slug>/).
 * Each studio therefore gets an independent worker with its own caches. A waiting
 * update is announced through `onUpdate`; the user decides when to reload.
 */
let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

export function registerStudioWorker(slug: string, onUpdate: (apply: () => void) => void) {
  if (registrationPromise) return registrationPromise;
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) {
    registrationPromise = Promise.resolve(null);
    return registrationPromise;
  }
  const scope = `/s/${slug}/`;
  registrationPromise = navigator.serviceWorker
    .register('/sw.js', { scope, updateViaCache: 'none' })
    .then((registration) => {
      // Reload only for an update the user accepted. The first install also fires
      // `controllerchange` (clients.claim) and must not reload a half-filled form.
      let updateAccepted = false;
      const announce = (worker: ServiceWorker) =>
        onUpdate(() => {
          updateAccepted = true;
          worker.postMessage({ type: 'SKIP_WAITING' });
        });
      if (registration.waiting && navigator.serviceWorker.controller) announce(registration.waiting);
      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        installing?.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) announce(installing);
        });
      });
      let reloading = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloading || !updateAccepted) return;
        reloading = true;
        window.location.reload();
      });
      return registration;
    })
    .catch((error: unknown) => {
      console.warn('[sw] registration failed', error);
      return null;
    });
  return registrationPromise;
}

export async function studioRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (registrationPromise) return registrationPromise;
  if (!('serviceWorker' in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration(window.location.pathname)) ?? null;
}

/** Owner sign-out: ask the worker to drop anything private (nothing is cached by design). */
export async function clearPrivateWorkerCaches() {
  try {
    const registration = await studioRegistration();
    registration?.active?.postMessage({ type: 'CLEAR_PRIVATE' });
  } catch {
    // ignore
  }
}
